import React from "react";
import Modal, { ModalProps } from "components/Modal";
import {
  ComponentDetailsByFileOut,
  InventoryProductDetailsByFileOut,
  ProductDisabledStatusEnum,
  ProductSourceEnum,
  getLocalAuthHeader,
} from "api-client";
import { useParams } from "react-router";
import { IconCancel, IconCube, IconStack2 } from "@tabler/icons-react";
import ComponentOverview from "./features/ComponentOverview";
import { observer } from "mobx-react-lite";
import { searchStore } from "features/ProductSearch/SearchStore";
import clsx from "clsx";
import DetailsView from "./features/DetailsView";
import PoductHeaderActions from "./components/PoductHeaderActions";
import Tooltip from "components/Tooltip";
import { productSearchStore } from "features/AdvancedProductSearch/ProductSearchStore";
import ConfirmModal from "components/ConfirmModal";
import useProductActions from "features/AdvancedProductSearch/useProductActions";
import { useTranslation } from "react-i18next";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";

interface ProductDetailsModalProps extends ModalProps {
  productId?: string | null;
  productType?: string | null;
  productName?: string | null;
  isPublic?: boolean;
}

function ProductDetailsModal(props: ProductDetailsModalProps) {
  const { ifc_id } = useParams();
  const [details, setDetails] = React.useState<
    InventoryProductDetailsByFileOut | ComponentDetailsByFileOut
  >();
  const {
    openPublishConfirmModal,
    setOpenPublishConfirmModal,
    handleChangeProductStatus,
  } = useProductActions();
  const { t } = useTranslation();

  const isComponent = props?.productType == "COMPONENT";
  const canEdit =
    (details as InventoryProductDetailsByFileOut)?.permissions?.can_edit ??
    true;
  const canDuplicate =
    (details as InventoryProductDetailsByFileOut)?.permissions?.can_duplicate ??
    true;
  // Visibility of the edit button is bound to ownership: only products that
  // belong to the user's organisation expose it (whether it is then clickable
  // is bound to `canEdit` below).
  const productOrgId = (details as InventoryProductDetailsByFileOut)
    ?.organisation_id;
  const isOwnedByOrg =
    !!productOrgId && productOrgId === sidebarMenuStore.currentOrganisation?.id;

  async function getDetails() {
    const authHeader = getLocalAuthHeader();
    if (!props?.productId || !authHeader) return;
    const fetchedData =
      props?.productType === "PRODUCT"
        ? await searchStore.getProductDetails(props?.productId, ifc_id)
        : await searchStore.getComponentDetails(props?.productId, ifc_id);
    if (fetchedData) {
      setDetails(fetchedData);
      searchStore.setCurrentDetailNode(
        fetchedData as ComponentDetailsByFileOut
      );
    }
  }

  React.useEffect(() => {
    if (props.open) {
      getDetails();
    } else if (props?.productId === details?.id) {
      searchStore.setCurrentDetailNode();
      setDetails(undefined);
    }

    return () => {
      setDetails(undefined);
      searchStore.setNavigationIsSticky(false);
    };
  }, [props.open]);

  const imageURL = (details as InventoryProductDetailsByFileOut)?.files_info
    ?.images?.[0]?.url;

  const diagramURL = (details as InventoryProductDetailsByFileOut)?.files_info
    ?.diagrams?.[0]?.url;

  const checkComponentForAlertsRecursively = (
    item: ComponentDetailsByFileOut
  ) => {
    let hasAlert: boolean =
      item?.product_disabled_status != ProductDisabledStatusEnum.UpToDate ||
      false;
    if (hasAlert) return true;
    item.children?.forEach((rec) => {
      if (rec.product_disabled_status != ProductDisabledStatusEnum.UpToDate) {
        hasAlert = true;
      }
      if (rec.children?.length) checkComponentForAlertsRecursively(rec);
    });
    return hasAlert;
  };

  const isCompliant = searchStore.currentDetailNode?.is_compliant_by_file;

  const hasWarnings =
    !isCompliant ||
    searchStore.currentDetailNode?.product_disabled_status !=
      ProductDisabledStatusEnum.UpToDate ||
    checkComponentForAlertsRecursively(details as ComponentDetailsByFileOut);

  function getProductIcon(className?: string): React.ReactNode {
    return (
      <div className="flex items-center">
        {diagramURL ? (
          <img
            src={diagramURL}
            className="w-[60px] max-w-[60px] max-h-[60px] mr-4"
          />
        ) : null}
        {props?.productType === "COMPONENT" ? (
          <IconStack2
            stroke={3}
            className={clsx(
              "bg-cyan-300 rounded-full p-[5px] min-w-6 min-h-6 mr-2 text-cyan-900",
              className
            )}
          />
        ) : (
          <IconCube
            stroke={3}
            className={clsx(
              "bg-teal-300 rounded-full p-[5px] min-w-6 min-h-6 mr-2 text-teal-900",
              className
            )}
          />
        )}
      </div>
    );
  }

  function getTitle(): React.ReactNode {
    const isManufacturerSource =
      details?.dataset_source?.toLowerCase() ===
      ProductSourceEnum.Manufacturer.toLowerCase();

    const headerActions = (
      <PoductHeaderActions
        productId={props?.productId}
        isComponent={isComponent}
        canEdit={canEdit}
        canDuplicate={canDuplicate}
        isOwnedByOrg={isOwnedByOrg}
        isManufacturerLicence={productSearchStore.isManufacturerLicence}
        isManufacturerProduct={isManufacturerSource}
        productName={props?.productName}
        isPublic={props.isPublic}
        changeStatus={() =>
          handleChangeProductStatus(
            props.isPublic,
            props?.productName,
            props?.productId
          )
        }
      />
    );

    if (imageURL && !searchStore.navigationIsSticky) {
      return headerActions;
    }

    return (
      <div className="flex w-full justify-between items-center">
        {details?.name && (
          <Tooltip
            className="mr-3 max-w-[540px]"
            truncate
            content={details.name}
          >
            {details.name}
          </Tooltip>
        )}

        {headerActions}
      </div>
    );
  }

  const headerImageVisible = imageURL && !searchStore.navigationIsSticky;

  return (
    <>
      <Modal
        isOpen={props.open}
        setIsOpen={props.close}
        leadingIcon={headerImageVisible ? null : getProductIcon()}
        title={getTitle()}
        titleClassName={headerImageVisible ? "" : "w-full"}
        headerClassName={clsx(
          "h-[60px] px-[14px] !py-3",
          headerImageVisible
            ? "absolute top-0 py-3 pr-3 right-0 z-3 w-full flex bg-white/40 border-b-0 !justify-end"
            : "bg-gradient-to-r from-teal-50",
          {
            sticky: imageURL && searchStore.navigationIsSticky,
            "pl-0": diagramURL,
          }
        )}
        containerClassName={clsx(
          isComponent
            ? "min-w-[1120px] max-w-[1120px]"
            : "min-w-[768px] max-w-[768px]"
        )}
        closeButton
        disableOutsideClick
        closeClassName={clsx({
          "absolute top-0 py-3 pr-3 right-0 w-full h-[48px] bg-gradient-to-r":
            imageURL,
          "from-teal-50/50 to-white/50 text-gray-700 sticky": imageURL,
        })}
        closeIconClassName={clsx({
          "text-gray-700 hover:bg-transparent hover:text-gray-900": imageURL,
        })}
        className="p-0"
        position="top"
      >
        {details ? (
          <div className={clsx(isComponent ? "grid grid-cols-9" : "")}>
            {isComponent ? (
              <ComponentOverview
                details={details as ComponentDetailsByFileOut}
                hasWarnings={hasWarnings}
              />
            ) : null}

            <DetailsView
              hasWarnings={hasWarnings}
              isComponent={isComponent}
              details={details}
            />
          </div>
        ) : (
          skeleton()
        )}
      </Modal>

      <ConfirmModal
        open={openPublishConfirmModal}
        close={() => setOpenPublishConfirmModal(false)}
        title={t("productSearch.unpublishConfirmTitle", {
          product_name: props.productName,
        })}
        descriptionMesage={t("productSearch.unpublishConfirmDesc")}
        confirmButtonTitle={
          <div className="flex items-center">
            <IconCancel className="mr-2" />
            {t("productSearch.unpublishConfirmButton")}
          </div>
        }
        onConfirm={() =>
          handleChangeProductStatus(
            props.isPublic,
            props.productName,
            props.productId
          )
        }
      />
    </>
  );
}

export default observer(ProductDetailsModal);

function skeleton() {
  return (
    <div className="animate-pulse p-6">
      <div className="h-20 w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5"></div>
      <div className="h-20 w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5"></div>
      <div className="h-20 w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5"></div>
    </div>
  );
}
